import React, { useEffect, useMemo, useState } from "react";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericDateTimeRangePicker from "@ui-components/DateTime/GenericDateTimeRangePicker";
import MultiFilterDropdown, {
  type MultiFilterFieldConfig,
  type MultiFilterValue,
} from "@ui-components/Filter/MultiFilterDropdown";
import { humanizeResourceType } from "../AuditLogsData";
import {
  hasActiveAuditLogFilters,
  type AuditLogFiltersState,
} from "../utils/auditLogsUrlSync";
import type {
  AuditLogAction,
  AuditLogSource,
  AuditLogsFilterOptions,
} from "../types";

/* ──────────────────────────────────────────────────────────────────────────────
 * Local helpers
 * ──────────────────────────────────────────────────────────────────────────── */

const ACTION_LABEL: Record<AuditLogAction, string> = {
  create: "Create",
  update: "Update",
  delete: "Delete",
  associate: "Associate",
  dissociate: "Dissociate",
  bulk_update: "Bulk update",
  claim: "Claim",
};

const SOURCE_LABEL: Record<AuditLogSource, string> = {
  dashboard: "Dashboard",
  pipeline: "Pipeline",
  admin: "Admin",
  system: "System",
};

const ACTION_FIELD_ID = "action";

/**
 * Tiny debounce hook scoped to this module — debounces text inputs so we
 * don't refire `/audit-logs` on every keystroke. The repo doesn't ship
 * a generic one and we don't want to introduce a new shared util just
 * for this file.
 */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function isoToDate(iso: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/* ──────────────────────────────────────────────────────────────────────────────
 * Styled <select>
 * ──────────────────────────────────────────────────────────────────────────── */

interface FilterSelectProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
  /** Native browser tooltip on hover — used to explain disabled state. */
  title?: string;
}

const FilterSelect: React.FC<FilterSelectProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = "All",
  disabled = false,
  title,
}) => (
  <label className="flex min-w-0 flex-1 flex-col gap-1" title={title}>
    <span className="text-xs font-medium text-semantic-text-secondary">
      {label}
    </span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      title={title}
      className="h-9 min-w-0 rounded-md border border-semantic-border-secondary bg-semantic-bg-primary px-2 text-sm text-semantic-text-primary disabled:opacity-60"
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </label>
);

/* ──────────────────────────────────────────────────────────────────────────────
 * Filters bar
 * ──────────────────────────────────────────────────────────────────────────── */

interface AuditLogFiltersBarProps {
  value: AuditLogFiltersState;
  options: AuditLogsFilterOptions | null;
  optionsLoading: boolean;
  onChange: (patch: Partial<AuditLogFiltersState>) => void;
  onClear: () => void;
}

export const AuditLogFiltersBar: React.FC<AuditLogFiltersBarProps> = ({
  value,
  options,
  optionsLoading,
  onChange,
  onClear,
}) => {
  /* ── Product / Resource Type / Source: single-select dropdowns ─────── */

  const productOptions = useMemo(
    () =>
      (options?.products ?? []).map((p) => ({
        value: String(p.id),
        label: p.name,
      })),
    [options?.products]
  );

  // Tenants with a single audit-enabled product don't need a Product
  // dropdown — there's nothing to choose. We auto-pin the filter to that
  // product so the backend always sees an explicit `productId`, and the
  // user gets a small read-only badge instead of a dropdown.
  const onlyProduct = useMemo(() => {
    const products = options?.products ?? [];
    return products.length === 1 ? products[0] : null;
  }, [options?.products]);

  useEffect(() => {
    if (onlyProduct == null) return;
    if (value.productId !== onlyProduct.id) {
      onChange({ productId: onlyProduct.id });
    }
    // `onChange` is intentionally not in the dep array — it can change
    // identity per render and would cause an infinite loop here.
  }, [onlyProduct, value.productId]);

  const resourceTypeOptions = useMemo(
    () =>
      (options?.resourceTypes ?? []).map((t) => ({
        value: t,
        label: humanizeResourceType(t),
      })),
    [options?.resourceTypes]
  );

  const sourceOptions = useMemo(
    () =>
      (options?.sources ?? []).map((s) => ({
        value: s,
        label: SOURCE_LABEL[s] ?? s,
      })),
    [options?.sources]
  );

  /* ── Action: multi-select via MultiFilterDropdown ──────────────────── */

  const actionFields: MultiFilterFieldConfig[] = useMemo(
    () => [
      {
        id: ACTION_FIELD_ID,
        label: "Action",
        searchable: false,
        options: (options?.actions ?? []).map((a) => ({
          id: a,
          label: ACTION_LABEL[a] ?? a,
        })),
      },
    ],
    [options?.actions]
  );

  const actionValue: MultiFilterValue = useMemo(
    () => ({ [ACTION_FIELD_ID]: value.actions }),
    [value.actions]
  );

  const handleActionApply = (next: MultiFilterValue) => {
    const ids = (next[ACTION_FIELD_ID] ?? []) as AuditLogAction[];
    onChange({ actions: ids });
  };

  /* ── Date range ─────────────────────────────────────────────────────── */

  const dateRangeValue = useMemo(
    () => ({ start: isoToDate(value.from), end: isoToDate(value.to) }),
    [value.from, value.to]
  );

  const handleDateRangeChange = (next: {
    start: Date | null;
    end: Date | null;
  }) => {
    onChange({
      from: next.start ? next.start.toISOString() : null,
      to: next.end ? next.end.toISOString() : null,
    });
  };

  /* ── Text inputs (debounced) ───────────────────────────────────────── */

  const [emailDraft, setEmailDraft] = useState<string>(value.userEmail);
  const [searchDraft, setSearchDraft] = useState<string>(value.search);

  // Reseed local drafts when the URL changes from outside (e.g. Clear button,
  // browser back). Comparing raw value avoids cursor jumps during typing.
  useEffect(() => {
    setEmailDraft(value.userEmail);
  }, [value.userEmail]);
  useEffect(() => {
    setSearchDraft(value.search);
  }, [value.search]);

  const debouncedEmail = useDebouncedValue(emailDraft, 350);
  const debouncedSearch = useDebouncedValue(searchDraft, 350);

  // Only the debounced value drives the URL push. Comparing against the
  // current URL value avoids a redundant push when the URL was just
  // updated for an unrelated reason.
  useEffect(() => {
    if (debouncedEmail !== value.userEmail) {
      onChange({ userEmail: debouncedEmail });
    }
  }, [debouncedEmail, value.userEmail, onChange]);

  useEffect(() => {
    if (debouncedSearch !== value.search) {
      onChange({ search: debouncedSearch });
    }
  }, [debouncedSearch, value.search, onChange]);

  /* ── Render ────────────────────────────────────────────────────────── */

  const isActive = hasActiveAuditLogFilters(value);

  return (
    // The filters bar establishes its own stacking context so its
    // popups (date range calendar, MultiFilterDropdown card) paint above
    // the table beneath it. `GenericTable` uses up to z-30 on sticky
    // fixed columns, so we sit at z-40 here. The popups inside stay at
    // z-50 within this stacking context, which keeps them above the
    // bar's own form fields too.
    <div className="sticky top-0 z-40 mb-3 flex flex-shrink-0 flex-col gap-3 rounded-md border border-semantic-border-secondary bg-semantic-bg-primary p-3 shadow-sm">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {onlyProduct ? (
          <div className="flex min-w-0 flex-col gap-1">
            <span className="text-xs font-medium text-semantic-text-secondary">
              Product
            </span>
            <div className="inline-flex h-9 items-center rounded-md border border-semantic-border-secondary bg-semantic-bg-secondary px-3 text-sm text-semantic-text-primary">
              {onlyProduct.name}
            </div>
          </div>
        ) : (
          <FilterSelect
            label="Product"
            value={value.productId != null ? String(value.productId) : ""}
            onChange={(next) => {
              // Clear resourceType in the SAME patch so the audit-logs
              // list doesn't briefly query with a (product, resourceType)
              // pair that doesn't exist while `/filters/options` is
              // refetching for the new product. The page-level pruner
              // would also catch this, but only after the fetch resolves
              // — which causes a visible "no results" flash.
              onChange({
                productId: next === "" ? null : Number(next),
                resourceType: null,
              });
            }}
            options={productOptions}
            disabled={optionsLoading || productOptions.length === 0}
            placeholder="All products"
          />
        )}
        <FilterSelect
          label="Resource type"
          value={value.resourceType ?? ""}
          onChange={(next) =>
            onChange({ resourceType: next === "" ? null : next })
          }
          options={resourceTypeOptions}
          disabled={optionsLoading || resourceTypeOptions.length === 0}
          placeholder="All resource types"
          title={
            !optionsLoading && resourceTypeOptions.length === 0
              ? "No resource types available for this tenant"
              : undefined
          }
        />
        <FilterSelect
          label="Source"
          value={value.source ?? ""}
          onChange={(next) =>
            onChange({ source: next === "" ? null : (next as AuditLogSource) })
          }
          options={sourceOptions}
          disabled={optionsLoading || sourceOptions.length === 0}
          placeholder="All sources"
        />
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-xs font-medium text-semantic-text-secondary">
            Action
          </span>
          <MultiFilterDropdown
            fields={actionFields}
            value={actionValue}
            onApply={handleActionApply}
            triggerLabel="All actions"
            cardTitle="Filter by action"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-1 lg:col-span-1">
          <span className="text-xs font-medium text-semantic-text-secondary">
            Date range
          </span>
          <GenericDateTimeRangePicker
            label=""
            value={dateRangeValue}
            onChange={handleDateRangeChange}
            displayFormat="dd/MM/yyyy HH:mm"
          />
        </div>

        <label className="flex min-w-0 flex-col gap-1">
          <span className="text-xs font-medium text-semantic-text-secondary">
            User email
          </span>
          <GenericInput
            type="search"
            value={emailDraft}
            onChange={setEmailDraft}
            placeholder="contains…"
          />
        </label>

        <label className="flex min-w-0 flex-col gap-1">
          <span className="text-xs font-medium text-semantic-text-secondary">
            Search
          </span>
          <GenericInput
            type="search"
            value={searchDraft}
            onChange={setSearchDraft}
            placeholder="resource name or email…"
          />
        </label>
      </div>

      {isActive && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClear}
            className="text-xs font-medium text-semantic-text-brand hover:underline"
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
};

export default AuditLogFiltersBar;
